import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { MessageService } from 'primeng/api'
import { AuthService } from '@data/services/modules/auth/auth.service'

import { WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { WorkItem, WorkItemArea, WorkItemPriority, WorkItemType, WorkUserRef } from '../../data/interfaces/worklog.interface'
import { AREA_META, AREA_ORDER, EFFORT_POINTS, ORIGIN_META, PRIORITY_META, PRIORITY_ORDER, SEVERITY_META, TYPE_META } from '../../data/constants/worklog.constants'
import { BacklogGroup, buildBacklogGroups, flattenGroups, spreadRanks } from '../../data/utils/backlog-groups.util'
import { EpicWithStatus } from '../../data/interfaces/epic.interface'
import { EPIC_STATUS_META } from '@modules/private/roadmap/data/constants/roadmap.constants'

/**
 * Vista Lista del Backlog en DOS cubetas (ADR-0040 · D8): «Siguiente sprint» (comprometidas al
 * sprint en planeación) arriba y el pool (acumulado sin priorizar) abajo. Quien tiene el
 * permiso `prioritize` (Admin / equipo) prioriza **arrastrando** —dentro de una cubeta para
 * ordenar, de una a otra para comprometer o regresar—; para el resto es lectura.
 *
 * El orden se guarda en `rank` y el backend lista el Backlog por ese campo. Ese orden ES la
 * prioridad: lo de arriba se atiende antes.
 */
@Component({
  selector: 'app-worklog-backlog',
  templateUrl: './worklog-backlog.component.html',
  styleUrls: ['./worklog-backlog.component.scss'],
})
export class WorklogBacklogComponent implements OnInit {
  @Output() openItem = new EventEmitter<string>()

  pool: WorkItem[] = []
  next: WorkItem[] = []
  poolTotal = 0
  loading = true

  //* ── Agrupación por épica ──
  //* El Backlog se ordena por BLOQUES: una épica con sus tareas, o una tarea suelta. La épica es la
  //* unidad que se prioriza — si entra al sprint, entra entera — y por eso es también la unidad que
  //* se arrastra. Su orden interno no decide nada: eso se resuelve en el Tablero.
  poolGroups: BacklogGroup[] = []
  nextGroups: BacklogGroup[] = []
  /** Qué épicas están desplegadas. Colapsadas de inicio: el nombre basta para decidir. */
  expandedEpics = new Set<string>()
  private epics: EpicWithStatus[] = []

  /** Prioriza quien trae el permiso RBAC (Admin y el equipo). La UI solo ofrece lo ejecutable. */
  canPrioritize = false
  //* Estimar es del equipo (isDev): el backend descarta el valor si no lo es. La estimación vive
  //* aquí desde que se retiró la pestaña «Sprints» — y es su sitio natural: se estima la tarea
  //* justo cuando se compromete, no en otra pantalla.
  isDev = false
  /** Tarea sobre la que está abierto el panel de estimación. */
  estimating: WorkItem | null = null

  //* ── Arrastre ──
  //* `dragged` es el bloque en vuelo; `dropBeforeKey` el bloque ante el que caería y
  //* `dropEndBucket` la cubeta cuya zona de cola está apuntada. Solo uno de los dos últimos vive a
  //* la vez: son dos formas del mismo indicador de inserción.
  //! Lo que se arrastra es un BLOQUE, no una fila: mover una tarea de dentro de una épica no
  //! significa nada, y mover la épica sí. Una tarea sin épica es un bloque de una.
  dragged: BacklogGroup | null = null
  dropBeforeKey: string | null = null
  dropEndBucket: 'pool' | 'next' | null = null
  //* Un `dragend` puede ir seguido de un `click` en la fila de origen: sin esta bandera, soltar
  //* la tarea en su nueva posición abriría además su detalle.
  private justDragged = false
  //* El spinner solo tiene sentido la primera vez. En los refrescos posteriores desmontaba la
  //* lista entera —parpadeo, scroll al inicio y foco perdido— para acabar pintando casi lo mismo.
  private loaded = false

  readonly EFFORT_POINTS = EFFORT_POINTS

  filterType: WorkItemType | null = null
  filterArea: WorkItemArea | null = null
  filterPriority: WorkItemPriority | null = null
  //* "Solo las mías" = lo que YO reporté. Sustituye a la vista «Mis tareas»: con un solo
  //* desarrollador esa pestaña era redundante, pero la pregunta de quien reporta —«¿en qué va lo
  //* que pedí?»— sigue siendo real, y aquí vive junto al resto de filtros en vez de en su propia
  //* pantalla. Va al servidor: la lista se pide acotada a 200 y filtrar en el cliente escondería
  //* resultados sin decirlo.
  filterMine = false

  readonly TYPE_META = TYPE_META
  readonly AREA_META = AREA_META
  readonly PRIORITY_META = PRIORITY_META
  readonly SEVERITY_META = SEVERITY_META
  readonly ORIGIN_META = ORIGIN_META

  readonly typeOptions = [
    { label: 'Tareas', value: 'task' },
    { label: 'Bugs', value: 'bug' },
  ]
  readonly areaOptions = AREA_ORDER.map((a) => ({ label: AREA_META[a].label, value: a }))
  readonly priorityOptions = PRIORITY_ORDER.map((p) => ({ label: PRIORITY_META[p].label, value: p }))

  constructor(private worklog: WorklogService, private auth: AuthService, private messageService: MessageService) {}

  ngOnInit(): void {
    this.canPrioritize = this.auth.userHasAuthorization('worklog', 'prioritize')
    this.isDev = !!this.auth.getUser?.user?.isDev
    this.reload()
  }

  get hasFilters(): boolean {
    return !!(this.filterType || this.filterArea || this.filterPriority || this.filterMine)
  }

  /**
   * Con un filtro puesto la lista en pantalla es un subconjunto, y la posición se calcula contra
   * los vecinos visibles: lo que se ve al soltar no sería lo que queda guardado. Priorizar es un
   * acto sobre la fila completa, así que ahí el arrastre se apaga y se dice por qué.
   */
  get canDrag(): boolean {
    return this.canPrioritize && !this.hasFilters
  }

  reload(): void {
    this.loading = !this.loaded
    //* El catálogo de épicas es para la CABECERA: los puntos y las tareas de la épica completa, que
    //* dan la escala de lo que falta. Lo pendiente se suma de las tareas que están aquí.
    this.worklog.getEpics().subscribe((epics) => {
      this.epics = epics
      this.regroup()
    })
    //* Dos requests, una por cubeta: el backend resuelve `pool` (sprint null) y `next`
    //* (sprint en planeación) — el set es chico y el filtro fino sigue en cliente.
    this.worklog.getBacklog(1, 200, {}, 'pool', this.filterMine).subscribe((page) => {
      this.pool = this.applyFilters(page.data || [])
      this.poolTotal = page.totalItems
      this.loaded = true
      this.loading = false
      this.regroup()
    })
    this.worklog.getBacklog(1, 200, {}, 'next', this.filterMine).subscribe((page) => {
      this.next = this.applyFilters(page.data || [])
      this.regroup()
    })
  }

  /** Rehace los bloques desde las listas planas. Se llama tras cada cambio que altere el orden. */
  private regroup(): void {
    const color = (estado: string) => EPIC_STATUS_META[estado as keyof typeof EPIC_STATUS_META]?.color ?? null
    this.poolGroups = buildBacklogGroups(this.pool, this.epics, color)
    this.nextGroups = buildBacklogGroups(this.next, this.epics, color)
  }

  /** Despliega o repliega una épica. */
  toggleEpic(g: BacklogGroup): void {
    if (this.expandedEpics.has(g.key)) this.expandedEpics.delete(g.key)
    else this.expandedEpics.add(g.key)
  }

  isExpanded(g: BacklogGroup): boolean {
    return g.kind === 'loose' || this.expandedEpics.has(g.key)
  }

  /** «8 tareas · 21 pts pendientes de 59». Lo pendiente manda: es lo que se está priorizando. */
  groupMeta(g: BacklogGroup): string {
    const partes = [`${g.items.length} ${g.items.length === 1 ? 'tarea' : 'tareas'}`]
    if (g.pendingPoints) partes.push(g.totalPoints ? `${g.pendingPoints} pts pendientes de ${g.totalPoints}` : `${g.pendingPoints} pts`)
    else if (g.totalPoints) partes.push(`sin puntuar · ${g.totalPoints} pts en la épica`)
    return partes.join(' · ')
  }

  groupsOf(bucket: 'pool' | 'next'): BacklogGroup[] {
    return bucket === 'pool' ? this.poolGroups : this.nextGroups
  }

  private applyFilters(items: WorkItem[]): WorkItem[] {
    return items.filter(
      (it) =>
        (!this.filterType || it.type === this.filterType) &&
        (!this.filterArea || (it.areas || []).includes(this.filterArea)) &&
        (!this.filterPriority || it.priority === this.filterPriority)
    )
  }

  clearFilters(): void {
    this.filterType = null
    this.filterArea = null
    this.filterPriority = null
    this.filterMine = false
    this.reload()
  }

  // ─────────── Priorización por arrastre (Admin / equipo) ───────────
  //* El orden de la lista ES la prioridad: la de arriba se atiende antes. Se fija arrastrando,
  //* que es la forma directa de decir "esta va antes que aquella" — antes eran tres botones por
  //* fila (subir, bajar, comprometer) y hacían falta N clics para mover una tarea N lugares.
  //*
  //* Cada fila es a la vez origen y destino: soltar sobre una fila inserta ANTES de ella, y por
  //* eso existe además una zona de cola al final de cada cubeta para el caso "al final de todo".

  onDragStart(g: BacklogGroup): void {
    if (!this.canDrag) return
    this.dragged = g
  }

  onDragEnd(): void {
    //* El `click` que a veces sigue al `dragend` llega en el mismo turno del event loop; basta
    //* con soltar la bandera en el siguiente para que la fila vuelva a abrir su detalle.
    this.justDragged = true
    this.clearDrag()
    setTimeout(() => (this.justDragged = false))
  }

  onEnterRow(g: BacklogGroup): void {
    if (!this.dragged) return
    this.dropBeforeKey = g.key
    this.dropEndBucket = null
  }

  onEnterEnd(bucket: 'pool' | 'next'): void {
    if (!this.dragged) return
    this.dropEndBucket = bucket
    this.dropBeforeKey = null
  }

  /** Suelta el bloque arrastrado justo ANTES de `target`. */
  onDropBefore(target: BacklogGroup, bucket: 'pool' | 'next'): void {
    const g = this.dragged
    this.clearDrag()
    if (!g || g.key === target.key) return
    this.moveGroup(g, bucket, this.groupsOf(bucket).indexOf(target))
  }

  /** Suelta el bloque arrastrado al final de la cubeta. */
  onDropEnd(bucket: 'pool' | 'next'): void {
    const g = this.dragged
    this.clearDrag()
    if (!g) return
    this.moveGroup(g, bucket, this.groupsOf(bucket).length)
  }

  /**
   * Atajos de teclado sobre la fila enfocada. El arrastre no es alcanzable con teclado, y quitar
   * los botones de flechas dejaría la priorización solo para el ratón.
   * `Alt+↑/↓` mueve un lugar · `Alt+→` compromete al siguiente sprint · `Alt+←` regresa al backlog.
   */
  onRowKey(ev: KeyboardEvent, g: BacklogGroup, bucket: 'pool' | 'next'): void {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault()
      //* En una épica el Enter despliega; en una tarea suelta abre su detalle.
      if (g.kind === 'epic') this.toggleEpic(g)
      else this.openDetail(g.items[0])
      return
    }
    if (!this.canDrag || !ev.altKey) return
    const lista = this.groupsOf(bucket)
    const i = lista.indexOf(g)
    if (i < 0) return

    switch (ev.key) {
      case 'ArrowUp':
        if (i === 0) return
        ev.preventDefault()
        //* Insertar ante el vecino de arriba = subir un lugar.
        this.moveGroup(g, bucket, i - 1)
        return
      case 'ArrowDown':
        if (i >= lista.length - 1) return
        ev.preventDefault()
        //* Insertar ante el que está DOS abajo = bajar un lugar (el de en medio queda arriba).
        this.moveGroup(g, bucket, i + 2)
        return
      case 'ArrowRight':
        if (bucket !== 'pool') return
        ev.preventDefault()
        this.moveGroup(g, 'next', this.nextGroups.length)
        return
      case 'ArrowLeft':
        if (bucket !== 'next') return
        ev.preventDefault()
        this.moveGroup(g, 'pool', 0)
        return
    }
  }

  /** Abre el detalle, salvo que el clic sea el coletazo de un arrastre. */
  openDetail(it: WorkItem): void {
    if (this.justDragged) return
    this.openItem.emit(it._id)
  }

  /**
   * Mueve un BLOQUE a la posición `index` de `bucket` y persiste el orden nuevo.
   *
   * Las tareas del bloque se renumeran juntas entre los vecinos, conservando su orden relativo: así
   * la lista agrupada y el orden real nunca se contradicen, y al mirar la lista plana se ve
   * exactamente lo que se acaba de armar arrastrando.
   *
   * Se pinta primero y se guarda después —igual que el Tablero—: recargar las dos cubetas en cada
   * arrastre era medio segundo de lista en blanco para acabar mostrando lo que ya se veía.
   */
  private moveGroup(g: BacklogGroup, bucket: 'pool' | 'next', index: number): void {
    const from = this.poolGroups.includes(g) ? 'pool' : 'next'
    const src = this.groupsOf(from)
    const dest = this.groupsOf(bucket)
    const at = src.indexOf(g)
    if (at < 0) return

    //* El índice se corrige por el hueco que deja el propio bloque al salir: sin esto, arrastrar
    //* hacia abajo lo deja siempre un lugar antes de donde se soltó.
    let to = index
    if (src === dest && at < to) to -= 1
    if (src === dest && to === at) return

    const sinEl = src === dest ? dest.filter((x) => x !== g) : dest
    //* Los vecinos son el ÚLTIMO de arriba y el PRIMERO de abajo: el bloque entero cae en medio.
    const arriba = sinEl[to - 1]
    const abajo = sinEl[to]
    const ranks = spreadRanks(arriba ? arriba.items[arriba.items.length - 1].rank ?? null : null, abajo ? abajo.items[0].rank ?? null : null, g.items.length)

    if (!ranks) {
      //* Dos vecinos con la misma posición: no hay hueco que repartir. Pasa solo si el orden
      //* guardado quedó degenerado; escribir igual dejaría el bloque donde estaba sin decirlo.
      this.messageService.add({
        key: 'worklog',
        severity: 'warn',
        summary: 'Posiciones empatadas',
        detail: 'No se pudo calcular la posición nueva. Se recargó la lista; vuelve a intentarlo.',
        life: 5000,
      })
      this.reload()
      return
    }

    //! Comprometer una épica entera mueve TODAS sus tareas al sprint. Con Exigibilidad F1 son 33 de
    //! golpe y deshacerlo es arrastrarlas de vuelta una por una: se pregunta antes.
    if (from !== bucket && g.items.length > 1) {
      const destino = bucket === 'next' ? 'al siguiente sprint' : 'de vuelta al Backlog'
      if (!confirm(`¿Mover las ${g.items.length} tareas de «${g.title}» ${destino}?`)) return
    }

    const previos = g.items.map((it) => ({ it, rank: it.rank }))
    src.splice(at, 1)
    dest.splice(to, 0, g)
    g.items.forEach((it, i) => (it.rank = ranks[i]))
    g.rank = ranks[0]

    if (from !== bucket) {
      //* Las listas planas son la fuente para reagrupar: se mueven con el bloque.
      const desdeLista = this.listOf(from)
      const haciaLista = this.listOf(bucket)
      for (const it of g.items) {
        const j = desdeLista.indexOf(it)
        if (j >= 0) desdeLista.splice(j, 1)
        haciaLista.push(it)
      }
      this.poolTotal += bucket === 'pool' ? g.items.length : -g.items.length
    }
    this.syncFlat()

    this.worklog.reorderItems(
      g.items.map((it) => ({ id: it._id, rank: it.rank ?? 0 })),
      from !== bucket ? bucket : undefined
    ).subscribe({
      next: (res) => {
        if (!res) this.reload()
      },
      error: (err) => {
        for (const p of previos) p.it.rank = p.rank
        this.reload()
        this.messageService.add({
          key: 'worklog',
          severity: 'error',
          summary: 'No se pudo reordenar',
          detail: worklogErrorMessage(err, 'No se pudo guardar la posición nueva.'),
          life: 6000,
        })
      },
    })
  }

  /** Rehace las listas planas desde los bloques, para que el orden guardado y el visto coincidan. */
  private syncFlat(): void {
    this.pool = flattenGroups(this.poolGroups)
    this.next = flattenGroups(this.nextGroups)
  }



  private listOf(bucket: 'pool' | 'next'): WorkItem[] {
    return bucket === 'pool' ? this.pool : this.next
  }

  private clearDrag(): void {
    this.dragged = null
    this.dropBeforeKey = null
    this.dropEndBucket = null
  }

  // ─────────── Helpers ───────────
  //* El contexto de un ng-template es `any`: estos helpers tipan el acceso a los META desde
  //* la fila compartida (el template estricto rechaza indexar un Record con any).

  typeMeta(it: WorkItem) {
    return TYPE_META[it.type]
  }

  sevMeta(it: WorkItem) {
    return it.severity ? SEVERITY_META[it.severity] : null
  }

  prioMeta(it: WorkItem) {
    return it.priority ? PRIORITY_META[it.priority] : null
  }

  areaMeta(a: string) {
    return (AREA_META as any)[a]
  }

  userName(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '—'
    return `${u.names ?? ''} ${u.lastNames ?? ''}`.trim() || u.email || '—'
  }

  initials(u?: WorkUserRef | string | null): string {
    if (!u || typeof u === 'string') return '?'
    return `${(u.names ?? '?')[0] ?? ''}${(u.lastNames ?? '')[0] ?? ''}`.toUpperCase()
  }

  /** Identidad de un bloque: el slug de la épica, o el id de la tarea suelta. */
  trackGroup = (_: number, g: BacklogGroup): string => g.key

  /** Lo que anuncia un lector de pantalla al enfocar la fila: qué es y dónde está. */
  rowAria(it: WorkItem, g: BacklogGroup | null, bucket: 'pool' | 'next'): string {
    const cubeta = bucket === 'next' ? 'Siguiente sprint' : 'Backlog'
    const nombre = `${it.code ? it.code + ': ' : ''}${it.title}`
    if (!g) return `${nombre}. ${cubeta}.`
    //* La posición se anuncia sobre BLOQUES, que es la unidad que se ordena. Decir «3 de 81» cuando
    //* lo que se mueve es una épica entera sería mentir sobre lo que hace la flecha.
    const lista = this.groupsOf(bucket)
    const i = lista.indexOf(g)
    return `${nombre}. ${cubeta}, posición ${i + 1} de ${lista.length}.`
  }

  trackId(_: number, it: WorkItem): string {
    return it._id
  }

  /** Estimación al comprometer (ADR-0040 · D12): es la línea base del burndown del sprint. */
  estimate(it: WorkItem, points: number): void {
    this.worklog.prioritize(it._id, undefined, undefined, points).subscribe({
      next: (u) => {
        if (u) it.estimatedPoints = u.estimatedPoints
        this.estimating = null
      },
      error: (e) =>
        this.messageService.add({ key: 'worklog', severity: 'error', summary: 'No se pudo estimar', detail: worklogErrorMessage(e), life: 5000 }),
    })
  }
}
